import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { linkTo } from "../api/links";
import { useLoadSearch, useSearch, useVehicleSearch } from "../api/queries";
import type { SearchMatch } from "../api/types";
import { Badge, Empty, ErrorBox, Guid, Json, Panel, Spinner } from "../components/primitives";

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Hit {
  kind: string;
  guid: string;
  label: string;
  to: string | undefined;
  detail: unknown;
}

/** Where each kind of guid lives. `event` has no screen of its own — its batch is the story. */
function destination(match: SearchMatch): string | undefined {
  switch (match.kind) {
    case "load":
      return linkTo.load(match.guid);
    case "batch":
      return linkTo.batch(match.guid);
    case "driver":
      return linkTo.driver(match.guid);
    case "device":
      return linkTo.device(match.guid);
    case "event": {
      const batch = match.detail["batch_guid"];
      return typeof batch === "string" ? linkTo.batch(batch) : undefined;
    }
    default:
      return undefined;
  }
}

export function SearchPage() {
  const [params] = useSearchParams();
  const query = (params.get("q") ?? "").trim();
  const isGuid = GUID.test(query);
  const global = useSearch(query, isGuid);
  const loads = useLoadSearch(isGuid ? "" : query);
  const vehicles = useVehicleSearch(query);
  const navigate = useNavigate();

  const sources = isGuid ? [global, vehicles] : [loads, vehicles];
  const pending = sources.some((source) => source.isPending);
  const errors = sources.flatMap((source) => (source.error ? [source.error] : []));

  const hits: Hit[] = [
    ...(global.data?.matches ?? []).map((match) => ({
      kind: match.kind,
      guid: match.guid,
      label: match.label,
      to: destination(match),
      detail: match.detail,
    })),
    ...(isGuid ? [] : (loads.data?.loads ?? [])).map((load) => ({
      kind: "load",
      guid: load.guid,
      label: load.number ?? "load",
      to: linkTo.load(load.guid),
      detail: load,
    })),
    ...(vehicles.data?.vehicles ?? []).map((vehicle) => ({
      kind: "vehicle",
      guid: vehicle.guid,
      label: vehicle.vin ?? "vehicle",
      to: linkTo.load(vehicle.load_guid),
      detail: vehicle,
    })),
  ];

  // One unambiguous hit is the common case: go straight there instead of asking.
  const only = !pending && hits.length === 1 ? hits[0]!.to : undefined;
  useEffect(() => {
    if (only) navigate(only, { replace: true });
  }, [only, navigate]);

  return (
    <div className="mx-auto h-full max-w-3xl space-y-4 overflow-y-auto p-5">
      <Panel id="search" title={`Search · ${query}`}>
        {!query && <Empty>Paste a guid, a load number or a VIN into the box above.</Empty>}
        {query && pending && <Spinner label={isGuid ? "resolving guid" : "matching"} />}
        {errors.map((error, index) => (
          <ErrorBox key={index} error={error} />
        ))}

        {query && !pending && hits.length === 0 && errors.length === 0 && (
          <Empty>
            {isGuid ? (
              <>
                <p className="text-body">Nothing in the engine knows this guid.</p>
                <p className="mt-2 text-xs text-muted">
                  Either it belongs to another store (a carrier-monolith id, a legacy load without a
                  guid), or the driver's batch never reached the engine — check the driver's inbox
                  rather than the load.
                </p>
              </>
            ) : (
              <p className="text-body">
                No load number (in any letter case) or VIN equals this exactly.
              </p>
            )}
          </Empty>
        )}

        {hits.map((hit) => (
          <div key={`${hit.kind}-${hit.guid}`} className="row">
            <div className="flex items-center gap-3">
              <Badge tone="info">{hit.kind}</Badge>
              <span className="text-sm text-strong">{hit.label}</span>
              {hit.to && (
                <button type="button" className="btn ml-auto" onClick={() => navigate(hit.to!)}>
                  {hit.kind === "vehicle" ? "open load" : "open"}
                </button>
              )}
            </div>
            <div className="mt-1">
              <Guid value={hit.guid} to={hit.kind === "vehicle" ? undefined : hit.to} />
            </div>
            <div className="mt-2">
              <Json value={hit.detail} collapsed />
            </div>
          </div>
        ))}
      </Panel>
    </div>
  );
}
